//import external from "./../../externalModules.js";
import { BaseAnnotationTool } from "./base/BaseAnnotationTool.js";
// State
import { addToolState, getToolState } from "component/toolManager/toolState.js";
import toolStyle from "component/toolManager/toolStyle.js";
import toolColors from "component/toolManager/toolColors.js";
// Manipulators
import { moveNewHandle } from "component/molecules/viewport/core/manipulators/index.js";
// Drawing
import {
  getNewContext,
  draw,
  setShadow,
  drawJoinedLines,
} from "./../../draw/index.js";

import drawLinkedTextBox from "./../../draw/drawLinkedTextBox.js";
import drawArc from "./../../draw/drawArc.js";
import { textBoxWidth } from "./../../draw/drawTextBox.js";
import drawHandles from "./../../draw/drawHandles.js";
//import lineSegDistance from "component/util/lineSegDistance.js";
import math from "core/math";
import { angleCursor } from "../cursors/index.js";

import { getEnabledElement } from "component/toolManager/enabledElements.js";
import Helper from "helper";
/**
 * @public
 * @class AngleTool
 * @memberof Tools.Annotation
 * @classdesc Create and position an angle by placing three consecutive points.
 * @extends Tools.Base.BaseAnnotationTool
 * @hideconstructor
 *
 * @param {ToolConfiguration} [configuration={}]
 */
export default class AngleTool extends BaseAnnotationTool {
  constructor(configuration = {}) {
    const defaultConfig = {
      name: "angle",
      supportedInteractionTypes: ["Mouse", "Touch"],
      svgCursor: angleCursor,
    };
    const initialConfiguration = Object.assign(defaultConfig, configuration);

    super(initialConfiguration);

    this.initialConfiguration = initialConfiguration;
    this.preventNewMeasurement = false;
  }

  createNewMeasurement(eventData) {
    const { image } = eventData;
    //if (!image || !image.pixelSpacing) {
    // 1. 영상 데이터가 없다면, 그리기를 생성하지 않음
    if (!image) {
      return;
    }

    // 2. US 영상일 경우, Physical Delta/Physical Direction Unit
    // 이외 영상일 경우, PixelSpacing이 없다면 그리기를 생성하지 않음
    const modality = image.modality;
    if (
      Helper.Validator.isDefined(modality) === false ||
      Helper.Validator.isString(modality) === false
    ) {
      return;
    }

    if (modality.toLowerCase() === "us") {
      const units = Helper.DICOM.getPhysicalDirectionUnit(image);
      if (units.x === -1 || units.y === -1) {
        return;
      }

      const pixelDelta = Helper.DICOM.getPixelSpacingByModality(
        modality,
        image
      );
      if (pixelDelta.x === 0 || pixelDelta.y === 0) {
        return;
      }
    } else {
      if (
        image.isExistPixelSpacing === false && image.usePixelSpacingApproximateValue === false
      ) {
        return;
      }
    }
    // Create the measurement data for this tool with the end handle activated
    return {
      visible: true,
      active: true,
      color: undefined,
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        middle: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true,
        },
      },
      willbeRemove: true,
      moveCount: 0,
    };
  }

  pointNearTool(element, data, coords) {
    if (data.visible === false) {
      return false;
    }

    return (
      math.line.lineSegemntDistance(
        element,
        data.handles.start,
        data.handles.middle,
        coords
      ) < 25 ||
      math.line.lineSegemntDistance(
        element,
        data.handles.middle,
        data.handles.end,
        coords
      ) < 25
    );
  }

  renderToolData(evt) {
    const eventData = evt.detail;
    //const enabledElement = eventData.enabledElement;
    const enabledElement = eventData;
    const { handleRadius, drawHandlesOnHover } = this.configuration;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }
    const { image } = eventData;
    if (!image) {
      return;
    }
    const modality = image.modality;

    const pixelSpacing = Helper.DICOM.getPixelSpacingByModality(
      modality,
      image
    );
    if (!pixelSpacing.x || !pixelSpacing.y) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvas);

    const lineWidth = toolStyle.getToolWidth();

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false || data.willbeRemove === true) {
        continue;
      }

      draw(context, (context) => {
        setShadow(context, this.configuration);

        // Differentiate the color of activation tool
        const color = toolColors.getColorIfActive(data);

        const handleStartCanvas = math.coordinate.pixelToCanvas(
          eventData.element,
          data.handles.start
        );
        const handleMiddleCanvas = math.coordinate.pixelToCanvas(
          eventData.element,
          data.handles.middle
        );

        drawJoinedLines(
          context,
          eventData.element,
          data.handles.start,
          [data.handles.middle, data.handles.end],
          { color, lineWidth: 3 }
        );

        // Draw the handles
        const handleColor = toolColors.getHandleColorIfActive(data);
        const handleOptions = {
          color: handleColor,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
          lineWidth: 3,
        };

        drawHandles(context, eventData, data.handles, handleOptions);

        // Default to isotropic pixel size, update suffix to reflect this
        const columnPixelSpacing = pixelSpacing.y || 1;
        const rowPixelSpacing = pixelSpacing.x || 1;

        const sideA = {
          x:
            (data.handles.middle.x - data.handles.start.x) * columnPixelSpacing,
          y: (data.handles.middle.y - data.handles.start.y) * rowPixelSpacing,
        };

        const sideB = {
          x: (data.handles.end.x - data.handles.middle.x) * columnPixelSpacing,
          y: (data.handles.end.y - data.handles.middle.y) * rowPixelSpacing,
        };

        const sideC = {
          x: (data.handles.end.x - data.handles.start.x) * columnPixelSpacing,
          y: (data.handles.end.y - data.handles.start.y) * rowPixelSpacing,
        };

        const sideALength = length(sideA);
        const sideBLength = length(sideB);
        const sideCLength = length(sideC);

        // Cosine law
        let angle = Math.acos(
          (Math.pow(sideALength, 2) +
            Math.pow(sideBLength, 2) -
            Math.pow(sideCLength, 2)) /
            (2 * sideALength * sideBLength)
        );

        angle *= 180 / Math.PI;

        const primaryAngle = math.Basic.measureAngle(
          data.handles.middle.x,
          data.handles.middle.y,
          data.handles.start.x,
          data.handles.start.y
        );

        const secondaryAngle = math.Basic.measureAngle(
          data.handles.middle.x,
          data.handles.middle.y,
          data.handles.end.x,
          data.handles.end.y
        );

        let startAngle =
          secondaryAngle > primaryAngle ? secondaryAngle : primaryAngle;
        let endAngle =
          secondaryAngle > primaryAngle ? primaryAngle : secondaryAngle;

        const diffAngle = startAngle - endAngle;

        if (diffAngle > 1) {
          drawArc(
            context,
            eventData.element,
            data.handles.middle,
            startAngle,
            endAngle,
            20,
            { color, lineWidth: 3 }
          );
        } else {
          drawArc(
            context,
            eventData.element,
            data.handles.middle,
            endAngle,
            startAngle,
            20,
            { color, lineWidth: 3 }
          );
        }

        // if (isNaN(angle)) {
        //   angle = "0.00";
        // }

        data.rAngle = math.roundToDecimal(angle, 2);

        //if (Helper.Validator.isNumber(data.rAngle) && data.handles.end.active) {
        if (data.rAngle > 0) {
          const text = textBoxText(data, pixelSpacing.x, pixelSpacing.y);

          const distance = 15;

          let textCoords;

          if (!data.handles.textBox.hasMoved) {
            textCoords = {
              x: handleMiddleCanvas.x,
              y: handleMiddleCanvas.y,
            };

            const padding = 5;
            const textWidth = textBoxWidth(context, text, padding);

            if (handleMiddleCanvas.x < handleStartCanvas.x) {
              textCoords.x -= distance + textWidth + 10;
            } else {
              textCoords.x += distance;
            }

            const transform = math.getTransform(enabledElement);

            transform.invert();

            const coords = transform.transformPoint(textCoords.x, textCoords.y);

            data.handles.textBox.x = coords.x;
            data.handles.textBox.y = coords.y;
          }
          const linkColor = toolColors.getLinkColorIfActive(data);
          drawLinkedTextBox(
            context,
            eventData.element,
            data.handles.textBox,
            text,
            data.handles,
            textBoxAnchorPoints,
            color,
            linkColor,
            lineWidth,
            0,
            true
          );
        }
      });
    }

    function textBoxText(data, rowPixelSpacing, columnPixelSpacing) {
      const suffix =
        !rowPixelSpacing || !columnPixelSpacing ? " (isotropic)" : "";
      const str = "00B0"; // Degrees symbol

      return (
        data.rAngle.toString() + String.fromCharCode(parseInt(str, 16)) + suffix
      );
    }

    function textBoxAnchorPoints(handles) {
      return [handles.start, handles.middle, handles.end];
    }
  }

  addNewMeasurement(evt, interactionType) {
    const { image } = evt.detail.element;
    // 1. 영상 데이터가 없다면, 그리기를 생성하지 않음
    if (!image) {
      return;
    }

    // 2. US 영상일 경우, Physical Delta/Physical Direction Unit
    // 이외 영상일 경우, PixelSpacing이 없다면 그리기를 생성하지 않음
    const modality = image.modality;
    if (
      Helper.Validator.isDefined(modality) === false ||
      Helper.Validator.isString(modality) === false
    ) {
      return;
    }

    if (modality.toLowerCase() === "us") {
      const units = Helper.DICOM.getPhysicalDirectionUnit(image);
      if (units.x === -1 || units.y === -1) {
        return;
      }

      if (image.hasPhysicalDelta === false && image.isExistPixelSpacing === false){
        return;
      }
    } else {
      if (
        image.isExistPixelSpacing === false && image.usePixelSpacingApproximateValue === false
      ) {
        return;
      }
    }

    if (this.preventNewMeasurement) {
      return;
    }

    this.preventNewMeasurement = true;
    evt.preventDefault();
    evt.stopPropagation();

    const eventData = evt.detail;
    const measurementData = this.createNewMeasurement(eventData);
    //const element = evt.detail.element;
    const element = evt.detail.element.element;

    // Associate this data with this imageId so we can render it and manipulate it
    addToolState(element, this.name, measurementData);
    let enabledElement = getEnabledElement(element);
    enabledElement.needsRedraw = true;

    const doneMovingEndHandleOptions = Object.assign(
      {},
      {
        doneMovingCallback: () => {
          measurementData.active = false;
          this.preventNewMeasurement = false;
          let enabledElement = getEnabledElement(element);
          enabledElement.needsRedraw = true;
        },
      },
      this.options
    );

    const doneMovingMiddleHandleOptions = Object.assign(
      {},
      {
        doneMovingCallback: () => {
          measurementData.active = false;
          measurementData.handles.end.active = true;

          let enabledElement = getEnabledElement(element);
          enabledElement.needsRedraw = true;

          moveNewHandle(
            eventData,
            this.name,
            measurementData,
            measurementData.handles.end,
            doneMovingEndHandleOptions,
            interactionType
          );
        },
      },
      this.options
    );

    // Step 1, create start and second middle
    moveNewHandle(
      eventData,
      this.name,
      measurementData,
      measurementData.handles.middle,
      doneMovingMiddleHandleOptions,
      interactionType
    );
  }
}

function length(vector) {
  return Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2));
}
